import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Adds a single variant to the cart from a cart-line "Add <format>" upsell link.
 *
 * Unlike the standard product-form-component flow, this can't rely on a nested
 * <form> + submit: the cart line items are already wrapped in the cart's
 * <form action="/cart"> (snippets/cart-products.liquid), and nested forms are
 * invalid HTML — the browser discards the inner <form>, so the button ends up
 * submitting the outer cart form and navigates to /cart instead of adding via
 * JS. So we POST to cart/add directly and dispatch CartAddEvent (cart:update)
 * with the returned sections, which cart-items-component morphs in place.
 *
 * @typedef {object} CartLineUpsellRefs
 * @property {HTMLButtonElement} button - The "Add <format>" button.
 * @property {HTMLElement} [liveRegion] - Visually-hidden status region.
 *
 * @extends Component<CartLineUpsellRefs>
 */
class CartLineUpsellComponent extends Component {
  requiredRefs = ['button'];

  /** @param {Event} event */
  add(event) {
    event.preventDefault();

    const { button } = this.refs;
    const variantId = this.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    // Signal "processing" so the UI can show a loading state until the cart
    // re-renders (success morphs this away) or we re-enable on error.
    button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');

    // Ask cart/add to re-render every cart-items section on the page (cart page
    // or drawer) so the new line shows up without a full reload.
    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));
          window.publishStorefrontEvent?.('cart_upsell_add_failed', {
            component: 'cart_line_upsell',
            product_id: this.dataset.productId || null,
            variant_id: variantId,
            operation: 'add',
            error_type: 'server_rejected',
            response_status: response.status || null,
          });
          button.disabled = false;
          button.removeAttribute('aria-busy');
          return;
        }

        if (this.refs.liveRegion) this.refs.liveRegion.textContent = Theme.translations?.added ?? '';

        // cart-items-component listens for this on `document` and morphs the cart
        // section, which replaces this component — no need to re-enable the button.
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'cart-line-upsell',
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        window.publishStorefrontEvent?.('cart_upsell_add_failed', {
          component: 'cart_line_upsell',
          product_id: this.dataset.productId || null,
          variant_id: variantId,
          operation: 'add',
          error_type: 'network',
        });
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }
}

if (!customElements.get('cart-line-upsell-component')) {
  customElements.define('cart-line-upsell-component', CartLineUpsellComponent);
}
